import { useQueries } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useMemo, useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_SCROLL_PARAM = 'scroll'
const DEFAULT_PAGE_SIZE = 20

const indexToPage = (index: number, pageSize: number): number => Math.floor(index / pageSize) + 1

export type PageResponse<T> = {
    items: T[]
    total?: number
    pages?: number
}

export type FetchPageContext = {
    tabFromUrl: number
}

export type UseInfiniteScrollTableOptions<T> = {
    queryKey: readonly unknown[]
    fetchPage: (page: number, context?: FetchPageContext) => Promise<PageResponse<T>>
    pageSize?: number
    enabled?: boolean
    scrollParam?: string
    tabParam?: string
    defaultTab?: number
    /** Доп. правки query в той же транзакции, что и смена вкладки (скролл уже сброшен). */
    mutateSearchParamsOnTabChange?: (next: URLSearchParams, tabIndex: number) => void
}

const parseScrollFromUrl = (searchParams: URLSearchParams, param: string): number => {
    const value = searchParams.get(param)
    if (!value) return 0
    const scroll = parseInt(value, 10)
    return Number.isFinite(scroll) && scroll >= 0 ? scroll : 0
}

const parseTabFromUrl = (searchParams: URLSearchParams, param: string, defaultTab: number): number => {
    const value = searchParams.get(param)
    if (!value) return defaultTab
    const tab = parseInt(value, 10)
    return Number.isFinite(tab) && tab >= 0 ? tab : defaultTab
}

export const useInfiniteScrollTable = <T>({
    queryKey,
    fetchPage,
    pageSize = DEFAULT_PAGE_SIZE,
    enabled = true,
    scrollParam = DEFAULT_SCROLL_PARAM,
    tabParam,
    defaultTab = 0,
    mutateSearchParamsOnTabChange,
}: UseInfiniteScrollTableOptions<T>) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const scrollFromUrl = parseScrollFromUrl(searchParams, scrollParam)
    const tabFromUrl = tabParam ? parseTabFromUrl(searchParams, tabParam, defaultTab) : defaultTab

    const [visibleIndices, setVisibleIndices] = useState<number[]>([])
    const pendingTabRef = useRef<number | null>(null)
    const restoreScrollRef = useRef(scrollFromUrl > 0 ? scrollFromUrl : 0)

    useEffect(() => {
        if (scrollFromUrl === 0) restoreScrollRef.current = 0
    }, [scrollFromUrl])

    useEffect(() => {
        if (tabParam && pendingTabRef.current !== null && tabFromUrl === pendingTabRef.current) {
            pendingTabRef.current = null
        }
    }, [tabParam, tabFromUrl])

    const requiredPages = useMemo(() => {
        const pages = new Set<number>()
        if (scrollFromUrl > 0) {
            pages.add(indexToPage(scrollFromUrl, pageSize))
        }
        for (const idx of visibleIndices) {
            pages.add(indexToPage(idx, pageSize))
        }
        if (pages.size === 0) pages.add(1)
        return Array.from(pages).sort((a, b) => a - b)
    }, [visibleIndices, scrollFromUrl, pageSize])

    const fetchContext = tabParam ? { tabFromUrl } : undefined
    const queries = useQueries({
        queries: requiredPages.map(page => ({
            queryKey: [...queryKey, ...(tabParam ? [tabFromUrl] : []), page],
            queryFn: () => fetchPage(page, fetchContext),
            enabled,
        })),
    })

    const { allItems, totalCount, totalPages } = useMemo(() => {
        const pagesMap = new Map<number, PageResponse<T>>()
        let totalFromApi = 0

        for (let i = 0; i < queries.length; i++) {
            const data = queries[i].data
            const page = requiredPages[i]
            if (data) {
                pagesMap.set(page, data)
                totalFromApi = data.total ?? totalFromApi
            }
        }

        const total = totalFromApi || 0
        const items: (T | undefined)[] = Array(total).fill(undefined)

        for (const page of requiredPages) {
            const data = pagesMap.get(page)
            if (data?.items) {
                const start = (page - 1) * pageSize
                for (let i = 0; i < data.items.length && start + i < total; i++) {
                    items[start + i] = data.items[i]
                }
            }
        }

        const pagesFromApi = requiredPages.map(p => pagesMap.get(p)?.pages).find((p): p is number => p != null) ?? 0

        return { allItems: items, totalCount: total, totalPages: pagesFromApi }
    }, [queries, requiredPages, pageSize])

    const updateUrlScroll = useCallback(
        (scroll: number) => {
            const currentScroll = searchParams.get(scrollParam)
            const newScroll = scroll <= 0 ? null : String(scroll)
            if (currentScroll === newScroll) return

            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev.toString())
                    if (scroll <= 0) {
                        next.delete(scrollParam)
                    } else {
                        next.set(scrollParam, String(scroll))
                    }
                    return next
                },
                { replace: true }
            )
        },
        [searchParams, scrollParam, setSearchParams]
    )

    const onVisibleIndicesChange = useCallback(
        (indices: number[], scrollIndex: number) => {
            setVisibleIndices(prev => {
                if (prev.length !== indices.length) return indices
                if (prev.every((p, i) => p === indices[i])) return prev
                return indices
            })
            const firstVisible = indices.length > 0 ? scrollIndex : 0
            if (pendingTabRef.current !== null) return
            if (firstVisible < restoreScrollRef.current && restoreScrollRef.current > 0) return
            if (firstVisible >= restoreScrollRef.current) restoreScrollRef.current = 0
            updateUrlScroll(firstVisible)
        },
        [updateUrlScroll]
    )

    const handleTabChange = useCallback(
        (index: number) => {
            if (!tabParam) return
            pendingTabRef.current = index
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (index === defaultTab) next.delete(tabParam)
                    else next.set(tabParam, String(index))
                    next.delete(scrollParam)
                    mutateSearchParamsOnTabChange?.(next, index)
                    return next
                },
                { replace: false }
            )
        },
        [tabParam, scrollParam, setSearchParams, defaultTab, mutateSearchParamsOnTabChange]
    )

    const scrollToIndex = pendingTabRef.current !== null ? 0 : scrollFromUrl > 0 ? scrollFromUrl : undefined

    /** Пустой список и активная загрузка страницы (без «жёсткого» режима — его решает экран, см. ProductList). */
    const isLoading = totalCount === 0 && queries.some(q => q.isLoading)
    const error = queries.find(q => q.error)?.error as Error | undefined
    const isFetching = queries.some(q => q.isFetching)
    const isFetched = queries.some(q => q.isFetched)

    return {
        allItems,
        totalCount,
        totalPages,
        isLoading,
        error,
        isFetching,
        isFetched,
        scrollFromUrl,
        scrollToIndex,
        onVisibleIndicesChange,
        updateUrlScroll,
        tabFromUrl,
        handleTabChange,
        requiredPages,
    }
}
